import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { cloneDeep, isEmpty, isEqual, isNull } from "lodash";

import ExcelExportButton from "@plannel/components/ExcelExportButton";
import inventoryExceptionsService from "@plannel/services/rp/inventory-exceptions-service";
import { RPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";
import Snackbar from "@plannel/components/Snackbar";
import { SettingsButton } from "@plannel/components/ActionIconButton";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import debug from "@plannel/utils/debug-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import DimensionModal from "@plannel/components/modal/DimensionModal";
import locationService from "@plannel/services/data/location-service"

import FilterContainer from "@plannel/components/layout/FilterContainer";
import LocationTreeFilter from "@plannel/components/filter/LocationTreeFilter";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import RpVersionFilter from "@plannel/components/filter/RpVersionFilter";

const { snackBarMessageColor } = ColorUtil;
const { versionStatusCode } = CodeUtil;
const { SIMULATION, PLANNED, RELEASED } = versionStatusCode;
const InventoryExceptionsDetail = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();
  const { isPermitted } = useMenuAccess();

  const [rpVersionFilter, setRpVersionFilter] = useState(reduxViewState?.rpVersion ? reduxViewState.rpVersion : null);
  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? { locations: null, category: null });
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);

  const [invExceptionsDetail, setInvExceptionsDetail] = useState([]);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const gridRef = useRef();
  const columnDefs = useRef();
  const exportColumnDefs = useRef();

  const [dimensionModalOpen, setDimensionModalOpen] = useState(false);
  const [dimensions, setDimensions] = useState([]);
  const allDimensions = ["categoryNmGrp", "itemCd", "itemNm"];
  const headerNameByField ={"categoryNmGrp": "category", "itemCd": "itemCd", "itemNm": "itemName"};
  const requiredDimensions = ["locNmGrp", "locNm"];

  const [locations, setLocations] = useState([]);

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const defaultColumnDefs = [
    {
      headerName: 'location', colId: "locNmGrp", width: 150, pinned: "left", lockPinned: true, showRowGroup: "locNm",
      type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true }
    },
    {
      headerName: 'category', colId: "categoryNmGrp", width: 150, pinned: "left", lockPinned: true, showRowGroup: 'categoryNm',
      type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true }
    },
    { headerName: "locNm", field: "locNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    { headerName: "categoryNm", field: "categoryNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    { headerName: "itemCd", field: "itemCd", type: ["nonEditableColumn"], width: 180, pinned: "left", lockPinned: true, cellClass: 'stringType' },
    { headerName: "itemName", field: "itemNm", type: ["nonEditableColumn"], width: 180, pinned: "left", lockPinned: true, cellClass: 'stringType' }
  ];

  const defaultExportColumnDefs = [
    { field: "locNm", colId: "locNmGrp", rowGroup: true },
    { field: "categoryNm", colId: "categoryNmGrp", rowGroup: true },
    { headerName: "location", field: "locNm", width: 150 },
    { headerName: "category", field: "categoryNm", width: 150 },
    { headerName: "itemCd", field: "itemCd", width: 180, cellClass: 'stringType' },
    { headerName: "itemName", field: "itemNm", width: 180 }
  ];

  const [gridLoading, setGridLoading] = useState(false);
  const [p13nLoading, setP13nLoading] = useState(false);

  const getRequestParams = () => {
    const params = {};

    if (rpVersionFilter) {
      params.versionId = rpVersionFilter.id;
      params.bucket = rpVersionFilter.planConfigObj?.BUCKET_UNIT;
    }
    
    if (filterValue?.locations) {
      params.locCd = filterValue?.locations?.map(location => location?.locCd);
    }

    if (filterValue?.category) {
      params.categoryCd = filterValue.category.filter((category) => category.leafFlg).map((category) => category.hrchyCd);
    }

    if (extraItemCodesRef.current?.length > 0) {
      params.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      params.item = itemSearch.current;
    }

    return params;
  }

  /*
   * 예외 건수 셀 → 상세 화면. 대상은 **버전 상태에 따라 갈린다**(RELEASED=RP 계획검토 / 그 외=PSI 시뮬레이션)
   *   — 지금 열리는 그 화면 하나만 권한을 본다. 둘 다 묶어서 보면 쓸 수 있는 링크까지 사라진다.
   */
  const drillTargetKey = () => (rpVersionFilter?.statusCd === RELEASED ? "RP_REVIEW" : "PSI_SIMULATION");

  const exceptionCellStyle = params => {
    const { value } = params;
    if (value && value > 0 && isPermitted(drillTargetKey())) {
      return { color: 'blue', textDecoration: 'underline', cursor: 'pointer' };
    }

    return {};
  }

  const onExceptionClicked = params => {
    const { node, data, value, locations } = params;

    if (value && value > 0 && isPermitted(drillTargetKey())) {
      const reduxCategoryValue = {};
      const reduxItemValue = { itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current };
      const sortParams = { orderByColumn: "demandQty" };

      if (data) { // Location - Category - Item
        const { itemCd, locCd } = data;
        const destLocation = locations?.filter(location => location.locCd === locCd);
        if (!isEmpty(destLocation)) {
          reduxCategoryValue.destLocation = destLocation
        }
        reduxItemValue.onlyExceptionApplied = true;
        reduxItemValue.itemFilterList = {itemCd : [itemCd]}
      } else {  // Location or Location - Category
        const { field, allLeafChildren } = node;
        const baseChildren = allLeafChildren[0].data;
        const destLocation = locations?.filter(location => location.locCd === baseChildren.locCd);
        reduxItemValue.itemFilterList = {}
        if (!isEmpty(destLocation)) {
          reduxCategoryValue.destLocation = destLocation
        }
        if (isEqual(field, "categoryNm")) {
          reduxCategoryValue.category = [{ hrchyCd: baseChildren.categoryCd, name: baseChildren.categoryNm, leafFlg: true }];
        }
        reduxItemValue.onlyExceptionApplied = true;
      }

      /* 
      * Version Status에 따라 Link되는 화면 변경
      * Release: RP Reveiew
      * Simulation: PSI Simulation
      * */
      let reduxKey = "PSI_SIMULATION";
      if (rpVersionFilter?.statusCd === RELEASED) {
        reduxKey = "RP_REVIEW"
      }

      const reduxState = reduxUtil.createObjectState(reduxKey, { filter: reduxCategoryValue, ...reduxItemValue, rpVersion: rpVersionFilter, sortParams });

      reduxDispatch(reduxUtil.updateViewState(reduxState));
      reduxDispatch(reduxUtil.updateHistoryState(reduxKey));
    }
  }

  const setColumnByDimensionData = (colDefs) => {
    const requiredCols = requiredDimensions.concat(dimensions);

    if (dimensions.indexOf("categoryNmGrp") > -1) {
      requiredCols.push("categoryNm");
    }

    colDefs = colDefs.filter(columnDefs => {
      if (Object.keys(columnDefs).includes('colId')) {
        return requiredCols.indexOf(columnDefs.colId) > -1;
      } else {
        return requiredCols.indexOf(columnDefs.field) > -1; 
      }
    });

    return colDefs;
  }

  const setGridHeaders = (headers, headerMap, locations) => {
    if (headers.length <= 0) {
      return;
    }

    const colDefs = setColumnByDimensionData(cloneDeep(defaultColumnDefs));
    const exportColDefs = setColumnByDimensionData(cloneDeep(defaultExportColumnDefs));
    const defaultNumColDef = { width: 110, type: ["numberColumn", "nonEditableColumn", "rightAligned"], aggFunc: 'sum' };
    headers.forEach((header) => {
      const colDef = {
        headerName: headerMap[header], field: header,
        colId: GridUtils.IGNORE_P13N_COLLID + '_' + header,
        ...defaultNumColDef,
        onCellClicked: (params) => onExceptionClicked({ ...params, locations }),
        cellStyle: exceptionCellStyle
      };
      
      if (reduxViewState?.baseDate && isEqual(header, reduxViewState.baseDate)) {
        colDef.sort = "desc";
      }

      exportColDefs.push(colDef);
      colDefs.push(colDef);
    });

    colDefs.push({ 
      headerName: "total", field: "exceptions",
      colId: GridUtils.IGNORE_P13N_COLLID + '_total',
      ...defaultNumColDef 
    });

    exportColDefs.push({ 
      headerName: "total", field: "exceptions",
      colId: GridUtils.IGNORE_P13N_COLLID + '_total_export',
      ...defaultNumColDef 
    });

    columnDefs.current = colDefs;
    exportColumnDefs.current = exportColDefs;

    if (gridLoading) {
      // 차원 설정으로 껐던 컬럼이 되살아나면 맨 뒤로 밀리므로 columnDefs 순서를 지켜준다
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs: colDefs, viewName, initState: true, alignOrderToDefs: true });

      const reduxState = reduxUtil.createObjectState(viewName, { gridColumns : colDefs.map(columnDefs => {
        if (Object.keys(columnDefs).includes('colId')) {
          return columnDefs.colId;
        } else {
          return columnDefs.field;
        }
      }) });

      reduxDispatch(reduxUtil.updateViewState({ ...reduxState }));
    }
  }

  const setGridDatas = (headers, results) => {
    const datas = [];
    const exceptionPivotData = "exceptionPivotData";

    for (const resultsData of results) {
      for (let idx = 0, len = headers.length; idx < len; idx++) {
        const header = headers[idx];
        resultsData[header] = resultsData[exceptionPivotData][idx];
      }

      datas.push(resultsData);
    }

    return datas;
  }

  const retrieveLocation = (localDateHeader, headerMap, data) => {
    locationService.getAll({ pagination: false })
      .then(response => {
        setLocations(response.data.results);
        setGridHeaders(localDateHeader, headerMap, response.data.results);
        setInvExceptionsDetail(setGridDatas(localDateHeader, data));
      })
      .catch(e => {
        debug(e);
      })
  }

  const retrieveExceptionsDetail = () => {
    gridRef.current?.api?.setGridOption('loading', true);

    columnDefs.current = [];
    exportColumnDefs.current = [];
    const params = getRequestParams();
    inventoryExceptionsService.getAllDetail(params)
      .then(response => {
        const { pivotResult } = response.data;
        const { localDateHeader, headerMap, data } = pivotResult;

        if (data && !isEmpty(data)) {
          if (isEmpty(locations)) {
            retrieveLocation(localDateHeader, headerMap, data);
          } else {
            setGridHeaders(localDateHeader, headerMap, locations);
            setInvExceptionsDetail(setGridDatas(localDateHeader, data));
          }
        
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
          const colDefs = setColumnByDimensionData(cloneDeep(defaultColumnDefs));
          const exportColDefs = setColumnByDimensionData(cloneDeep(defaultExportColumnDefs));

          columnDefs.current = colDefs;
          exportColumnDefs.current = exportColDefs;

          GridUtils.setColumnDefs({ ...gridRef.current, columnDefs: colDefs, viewName, initState: true, alignOrderToDefs: true });
          setInvExceptionsDetail([]);
        }
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { filter: filterValue, rpVersion: rpVersionFilter, itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current });
        reduxDispatch(reduxUtil.updateViewState(reduxState));

        gridRef.current?.api?.setGridOption('loading', false);
      });
  }

  const onGridReady = () => {
    setGridLoading(true);
  }

  const onFirstDataRendered = () => {
    if (reduxViewState?.baseDate) {
      const header = reduxViewState.baseDate;

      const nodes = [];
      gridRef.current.api.forEachNode(node => {
        const { field, aggData } = node;
        if (isEqual(field, "categoryNm") && !isNull(aggData[header])) {
          nodes.push(node);
        }
      });
      gridRef.current.api.flashCells({ rowNodes: nodes, columns: [header], flashDelay: 2000, fadeDelay: 1000 });

      const reduxState = reduxUtil.createObjectState(viewName, { baseDate: null });
      reduxDispatch(reduxUtil.updateViewState(reduxState));
    }
  }

  useEffect(() => {
    if (!isEmpty(rpVersionFilter) && p13nLoading) {
      retrieveExceptionsDetail();
    } else {
      setInvExceptionsDetail([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rpVersionFilter, filterValue, dimensions]);

  const applyDimensions = selectedDimensions => {
    setP13nLoading(true);
    setDimensions(selectedDimensions);
  }

  return (
    <>
      <FilterContainer>
        <RpVersionFilter viewName={viewName} rpVersionValue={rpVersionFilter} onChangeFilter={setRpVersionFilter} statusFilters={[SIMULATION, PLANNED, RELEASED]} />
        <LocationTreeFilter viewName={viewName} filterValue={filterValue?.locations?.map(location => location?.locCd)} onChangeFilter={setFilterValue} />
        <ItemHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue} enableMultiSelect selectLeafs/>
        <RPItemAutocomplete retrieve={retrieveExceptionsDetail} sx={{ mr: 1.5 }} searchValue={itemSearch} key={"ITEM"} extraCodesRef={extraItemCodesRef} />
        <Box sx={{ ml: "auto" }}>
          {/* setter 를 그대로 넘기면 클릭 **이벤트 객체**가 state 에 들어간다(truthy 라 열리긴 하지만
              MUI Dialog 가 open 이 boolean 이 아니라고 경고한다) — 명시적으로 true 를 넣는다 */}
          <SettingsButton label="dimensionSetting" size="medium" onClick={() => setDimensionModalOpen(true)} />
          <ExcelExportButton
            title={title}
            service={inventoryExceptionsService}
            columnDefs={exportColumnDefs.current}
            setExcelData={setGridDatas}
            params={getRequestParams()} />
        </Box>
      </FilterContainer>
      <Box className="ag-theme-balham grid-area" sx={{ mb: 0.5 }}>
        <AgGridReact
          ref={gridRef}
          rowData={invExceptionsDetail}
          {...defaultGridMemo}
          groupDisplayType={'custom'}
          animateRows
          suppressAggFuncInHeader
          groupDefaultExpanded={dimensions.length}
          onGridReady={onGridReady}
          onFirstDataRendered={onFirstDataRendered}
        />
      </Box>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <DimensionModal
        open={dimensionModalOpen}
        setOpen={setDimensionModalOpen}
        viewName={viewName}
        allDimensions={allDimensions}
        applyDimensions={applyDimensions}
        headerNameByField={headerNameByField}
      />
    </>
  )
};

export default withTranslation()(InventoryExceptionsDetail);